import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

// Phase 2Z.n — Coverage Guard V1 simplified rule editor.
//
// ONLY exposes the V1-supported fields:
//   - active toggle
//   - category
//   - team scope (all teams / specific team)
//   - days of week
//   - min headcount (optional)
//   - min % of scheduled minutes (optional)
//   - description (optional)
//   - coverage check (CG time-window Phase 1, June 2026):
//     "Day-level cover" (default, today's behaviour) or
//     "Specific time window" with start/end times.
//
// Strictly does NOT send any of:
//   max_headcount, max_coverage_pct, location_id, priority,
//   effective_from, effective_to
//
// start_time / end_time / coverage_period ship ONLY when the manager
// explicitly picks "Specific time window" (plus a one-off
// coverage_period: "day_level" when switching a flagged rule back).
// Legacy rules that carry times but were never flagged are left
// untouched on save, so their behaviour cannot silently change.
//
// The backend Pydantic model leaves all optional fields as
// Optional[None] so omitting them simply means "not set" — zero
// schema risk.

const DAYS = [
  { id: 0, label: "Mon" }, { id: 1, label: "Tue" },
  { id: 2, label: "Wed" }, { id: 3, label: "Thu" },
  { id: 4, label: "Fri" }, { id: 5, label: "Sat" },
  { id: 6, label: "Sun" },
];

export function CoverageGuardRuleEditor({
  open, onOpenChange, editing, categories, teams, onSaved,
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [teamScope, setTeamScope] = useState("all");  // "all" | "each" | teamId
  const [days, setDays] = useState([0, 1, 2, 3, 4]);
  const [minHeadcount, setMinHeadcount] = useState("");
  const [minPct, setMinPct] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [coverageCheck, setCoverageCheck] = useState("day_level");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || "");
      setDescription(editing.description || "");
      setCategoryId(editing.category_id || "");
      // Reconstruct the scope dropdown value from the persisted
      // pair (team_id, apply_per_team).
      if (editing.apply_per_team) setTeamScope("each");
      else setTeamScope(editing.team_id || "all");
      setDays(Array.isArray(editing.days_of_week) && editing.days_of_week.length > 0
        ? [...editing.days_of_week] : [0, 1, 2, 3, 4]);
      setMinHeadcount(editing.min_headcount != null ? String(editing.min_headcount) : "");
      setMinPct(editing.min_coverage_pct != null ? String(editing.min_coverage_pct) : "");
      setIsActive(editing.is_active !== false);
      setCoverageCheck(editing.coverage_period === "time_window"
        ? "time_window" : "day_level");
      setStartTime(editing.start_time || "");
      setEndTime(editing.end_time || "");
    } else {
      setName("");
      setDescription("");
      // Phase 2A Step 3 - seed default to the first WORK category
      // so a fresh rule cannot land on Break/Away by accident.
      setCategoryId(
        (categories || []).find((c) => c.kind === "work")?.category_id
        || "");
      setTeamScope("all");
      setDays([0, 1, 2, 3, 4]);
      setMinHeadcount("");
      setMinPct("");
      setIsActive(true);
      setCoverageCheck("day_level");
      setStartTime("");
      setEndTime("");
    }
  }, [open, editing, categories]);

  const toggleDay = (d) => {
    setDays((prev) => prev.includes(d)
      ? prev.filter((x) => x !== d)
      : [...prev, d].sort((a, b) => a - b));
  };

  const validate = () => {
    if (!name.trim()) {
      toast.error("Please give this rule a name.");
      return false;
    }
    if (!categoryId) {
      toast.error("Pick a category.");
      return false;
    }
    if (days.length === 0) {
      toast.error("Pick at least one day of the week.");
      return false;
    }
    const hc = minHeadcount === "" ? null : Number(minHeadcount);
    const pct = minPct === "" ? null : Number(minPct);
    if (hc !== null && (!Number.isFinite(hc) || hc < 0 || !Number.isInteger(hc))) {
      toast.error("Minimum people must be a whole number, 0 or more.");
      return false;
    }
    if (pct !== null && (!Number.isFinite(pct) || pct < 0 || pct > 100
      || !Number.isInteger(pct))) {
      toast.error("Minimum % must be a whole number between 0 and 100.");
      return false;
    }
    if (hc === null && pct === null) {
      toast.error("Set a minimum number of people for this rule.");
      return false;
    }
    if (coverageCheck === "time_window") {
      if (!startTime || !endTime) {
        toast.error("Pick a start and end time for the specific time window.");
        return false;
      }
      if (startTime >= endTime) {
        toast.error("Start time must be before end time.");
        return false;
      }
      if (hc === null) {
        toast.error("Specific time window needs a minimum number of people.");
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // V1 payload — only V1-supported fields. Unsupported fields
      // (max_*, start_time, end_time, location_id, priority,
      // effective_*) are deliberately NEVER sent.
      // Phase 2Z.n.1 — `apply_per_team` IS V1-supported (drives the
      // "Each team" scope option) and ships in the body.
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        category_id: categoryId,
        team_id: (teamScope === "all" || teamScope === "each")
          ? null : teamScope,
        apply_per_team: teamScope === "each",
        days_of_week: days,
        is_active: isActive,
        min_headcount: minHeadcount === "" ? null : Number(minHeadcount),
        min_coverage_pct: minPct === "" ? null : Number(minPct),
      };
      // CG time-window Phase 1 - the flag and times ship ONLY on an
      // explicit opt-in. Switching a flagged rule back records
      // "day_level"; times are left untouched (day-level ignores
      // them). Legacy unflagged rules never get their times changed
      // by this editor.
      if (coverageCheck === "time_window") {
        body.coverage_period = "time_window";
        body.start_time = startTime;
        body.end_time = endTime;
      } else if (editing?.coverage_period === "time_window") {
        body.coverage_period = "day_level";
      }
      if (editing?.coverage_id) {
        await api.patch(`/coverage-rules/${editing.coverage_id}`, body);
        toast.success("Coverage Guard rule updated.");
      } else {
        await api.post("/coverage-rules", body);
        toast.success("Coverage Guard rule created.");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail)
        || "Failed to save Coverage Guard rule.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px]"
        data-testid="coverage-guard-rule-editor"
      >
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit Coverage Guard rule" : "New Coverage Guard rule"}
          </DialogTitle>
          <DialogDescription>
            Set the minimum number of people needed for a category on selected days.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="cg-name" className="text-xs">Name</Label>
            <Input
              id="cg-name"
              data-testid="cg-rule-name"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Inbound weekday minimum"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger data-testid="cg-rule-category">
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {/* Phase 2A Step 3 - Work-only picker so a manager
                      cannot accidentally point a Coverage Guard rule
                      at Break/Away in V1. */}
                  {(categories || [])
                    .filter((c) => c.is_active !== false
                                    && c.kind === "work")
                    .map((c) => (
                      <SelectItem
                        key={c.category_id} value={c.category_id}
                      >
                        {c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Team scope</Label>
              <Select value={teamScope} onValueChange={setTeamScope}>
                <SelectTrigger data-testid="cg-rule-team-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teams</SelectItem>
                  <SelectItem value="each">Each team (evaluated separately)</SelectItem>
                  {(teams || []).map((t) => (
                    <SelectItem key={t.team_id} value={t.team_id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Days of week</Label>
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid="cg-rule-days">
              {DAYS.map((d) => {
                const on = days.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`cg-rule-day-${d.id}`}
                    onClick={() => toggleDay(d.id)}
                    className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                      on
                        ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                        : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs">Coverage check</Label>
            <Select value={coverageCheck} onValueChange={setCoverageCheck}>
              <SelectTrigger data-testid="cg-rule-coverage-check">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day_level">Day-level cover</SelectItem>
                <SelectItem value="time_window">Specific time window</SelectItem>
              </SelectContent>
            </Select>
            <div
              className="text-[11px] text-slate-500 mt-1"
              data-testid="cg-rule-coverage-check-help"
            >
              {coverageCheck === "time_window"
                ? "Checks continuous cover between the selected start and end times."
                : "Counts employees assigned to this work category at any point on the selected day."}
            </div>
          </div>
          {coverageCheck === "time_window" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cg-start-time" className="text-xs">Start time</Label>
                <Input
                  id="cg-start-time"
                  type="time"
                  data-testid="cg-rule-start-time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="cg-end-time" className="text-xs">End time</Label>
                <Input
                  id="cg-end-time"
                  type="time"
                  data-testid="cg-rule-end-time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Minimum people needed</Label>
              <Input
                type="number" min={0} step={1}
                data-testid="cg-rule-min-headcount"
                value={minHeadcount}
                onChange={(e) => setMinHeadcount(e.target.value)}
                placeholder="e.g. 2"
              />
            </div>
            {/* Phase 2Z.cleanup — % rule input hidden in V1 to keep
                Coverage Guard simple. Existing minPct values on
                already-saved rules are preserved (form state still
                round-trips them; the backend evaluator is unchanged).
                Hidden so a manager cannot create a new percentage
                rule that would compete with the headcount rule. */}
            {(editing && Number(minPct) > 0) && (
            <div data-testid="cg-rule-min-pct-block">
              <Label className="text-xs">Minimum % of scheduled minutes</Label>
              <Input
                type="number" min={0} max={100} step={1}
                data-testid="cg-rule-min-pct"
                value={minPct}
                onChange={(e) => setMinPct(e.target.value)}
                placeholder="e.g. 20"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Editing an existing percentage rule. New rules use the headcount field only.
              </div>
            </div>
            )}
          </div>
          <div className="text-[11px] text-slate-500">
            {coverageCheck === "time_window"
              ? "Example: Phones needs 1 person covering between 08:00 and 11:00. NextRota will flag any gap inside that window."
              : "Example: set 2 people for Inbound calls on weekdays. NextRota will flag any weekday where fewer than 2 people are scheduled."}
          </div>
          <div>
            <Label htmlFor="cg-desc" className="text-xs">Description (optional)</Label>
            <Input
              id="cg-desc"
              data-testid="cg-rule-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this rule protecting?"
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input
              id="cg-active"
              type="checkbox"
              checked={isActive}
              data-testid="cg-rule-active"
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            <Label htmlFor="cg-active" className="text-xs cursor-pointer">
              Active
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            data-testid="cg-rule-cancel"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            data-testid="cg-rule-save"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : (editing ? "Save changes" : "Create rule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CoverageGuardRuleEditor;
